import { Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Search } from 'lucide-react';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import _, { isEmpty } from 'underscore';

import { useGet } from 'hooks/use-get';

import Breadcrumbs from 'components/breadcrumbs';

import FilterSidebar from 'components/filter-sidebar';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import Pagination from 'components/pagination';
import Paper from 'components/paper';
import TextField from 'components/text-field';
import { usePagination } from 'hooks/use-pagination';
import FeedManagementTabsHeader from '../../components/feed-management-tabs-header';
import { getFeedManagementCustomers } from './api/get-customers';

type Props = {};

const ListCustomers: React.FC<Props> = () => {
  const history = useHistory();
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const PAGINATION_LIMIT = 20;
  const PAGE_NAME_ID = 'feedManagement.listCustomers';

  // STATE
  const [searchValue, setSearchValue] = useState('');
  const { currentPage, from, to, handlePagination } = usePagination(PAGINATION_LIMIT);

  // FETCH DATA
  const { data: customers, isLoading } = useGet(['getFeedManagementCustomers'], () => getFeedManagementCustomers());

  // HANDLERS
  const handleOnClick = (url: string) => {
    history.push(url);
  };

  // RENDERS
  const renderRows = () => {
    const rows = _.chain(customers)
      .filter((item) => (isEmpty(searchValue) ? true : item.name.toLowerCase().includes(searchValue.toLowerCase())))
      .sortBy((p) => p.name.toLowerCase())
      .value();
    return rows;
  };

  return (
    <>
      <FeedManagementTabsHeader
        intro={
          <Typography color="white">
            <FormattedMessage id="general.confidential" />
          </Typography>
        }
        utils={<Breadcrumbs />}
      />
      <Paper transparent>
        <FilterSidebar>
          <TextField
            label={intl.formatMessage({ id: 'general.search' })}
            value={searchValue}
            endAdornment={<Search size={18} />}
            onChange={(event) => {
              setSearchValue(event.target.value);
            }}
          />
        </FilterSidebar>

        <List
          options={[
            { id: 'name', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.nameLabel` }) },
            { id: 'actions', label: '', hide: !isMobile },
          ]}
          orderBy="name"
          isLoading={isLoading}
          noResult={{
            show: renderRows().length <= 0,
            text: <FormattedMessage id={`${PAGE_NAME_ID}.list.noResults`} />,
          }}>
          {renderRows()
            .slice(from, to)
            .map((row) => (
              <ListRow key={row.id} onClick={() => handleOnClick(`/feed-management/customers/${row.id}`)}>
                <ListCell align="left" sx={{ whiteSpace: 'break-spaces !important' }}>
                  {row.name}
                </ListCell>

                <ListCell align="right">
                  <Button fullWidth={isMobile} size="small" onClick={() => handleOnClick(`/feed-management/customers/${row.id}`)}>
                    <FormattedMessage id="general.edit" values={{ type: '' }} />
                  </Button>
                </ListCell>
              </ListRow>
            ))}
        </List>
        <Pagination currentPage={currentPage} pages={Math.ceil(renderRows().length / PAGINATION_LIMIT)} onClick={handlePagination} />
      </Paper>
    </>
  );
};

export default ListCustomers;
